import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function EditListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`)
      .then((r) => setForm({
        ...r.data,
        price: r.data.price ?? "",
        acreage: r.data.acreage ?? "",
        bedrooms: r.data.bedrooms ?? "",
        bathrooms: r.data.bathrooms ?? "",
        sqft: r.data.sqft ?? "",
      }))
      .catch((e) => { toast.error(getApiError(e)); navigate("/listings"); });
  }, [id, navigate]);

  if (!form) return <div className="min-h-screen bg-paper"><Navbar /><div className="p-10 text-center text-[color:var(--text-secondary)]">Loading…</div></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        listing_type: form.listing_type,
        address: form.address, city: form.city, state: form.state, zip_code: form.zip_code,
        price: form.price !== "" ? parseFloat(form.price) : null,
        acreage: form.acreage !== "" ? parseFloat(form.acreage) : null,
        bedrooms: form.bedrooms !== "" ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms !== "" ? parseFloat(form.bathrooms) : null,
        sqft: form.sqft !== "" ? parseInt(form.sqft) : null,
        description: form.description,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
      };
      await api.patch(`/listings/${id}`, payload);
      toast.success("Listing updated");
      navigate("/portal");
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this listing? It will no longer be visible.")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Listing removed");
      navigate("/portal");
    } catch (err) { toast.error(getApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="edit-listing-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Card className="border-black/5">
          <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
            <CardTitle className="font-serif text-3xl">Edit listing</CardTitle>
            <Button onClick={remove} variant="outline" className="border-[color:var(--fire-red)] text-[color:var(--fire-red)] hover:bg-[color:var(--fire-red)] hover:text-white" data-testid="edit-listing-delete-btn">
              Delete listing
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Listing type</Label>
                <Select value={form.listing_type} onValueChange={(v) => set("listing_type", v)}>
                  <SelectTrigger data-testid="edit-listing-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home_sale">Home for sale</SelectItem>
                    <SelectItem value="home_wanted">Home wanted</SelectItem>
                    <SelectItem value="land_sale">Land for sale</SelectItem>
                    <SelectItem value="land_wanted">Land wanted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Title</Label><Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-listing-title" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Address</Label><Input required value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="edit-listing-address" /></div>
                <div><Label>City</Label><Input required value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="edit-listing-city" /></div>
                <div><Label>State</Label><Input required value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="edit-listing-state" /></div>
                <div><Label>ZIP</Label><Input required value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid="edit-listing-zip" /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Price</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="edit-listing-price" /></div>
                <div><Label>Acreage</Label><Input type="number" value={form.acreage} onChange={(e) => set("acreage", e.target.value)} data-testid="edit-listing-acreage" /></div>
                <div><Label>Sqft</Label><Input type="number" value={form.sqft} onChange={(e) => set("sqft", e.target.value)} data-testid="edit-listing-sqft" /></div>
                <div><Label>Bedrooms</Label><Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} data-testid="edit-listing-bedrooms" /></div>
                <div><Label>Bathrooms</Label><Input type="number" step="0.5" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} data-testid="edit-listing-bathrooms" /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="edit-listing-description" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Contact email</Label><Input type="email" required value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="edit-listing-contact-email" /></div>
                <div><Label>Contact phone</Label><Input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} data-testid="edit-listing-contact-phone" /></div>
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="edit-listing-submit-btn">
                {busy ? "Saving…" : "Save changes"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
